class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.form?.addEventListener('submit', this.onSubmit.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  get form() {
    return this.querySelector('form');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  onSubmit(event) {
    const productType = this.searchProductTypes?.value;
    if (!productType) return;
    const query = this.getQuery();
    if (query.length === 0) return;
    if (query.startsWith('product_type:')) return;
    event.preventDefault();
    const params = new URLSearchParams();
    params.set('q', `product_type:${productType} AND ${query}`);
    params.set('options[prefix]', 'last');
    window.location.href = `${this.form.getAttribute('action')}?${params.toString()}`;
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => this.setLoadingState(false));
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

if (!customElements.get('smx-search-messages')) {
  customElements.define('smx-search-messages', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.input = this.parentElement.querySelector('input[type="search"]');
      this.output = this.querySelector('[data-smx-search-text]');
      this.messages = Array.from(this.querySelectorAll('[data-smx-search-message]'), node => Array.from(node.textContent.trim())).filter(message => message.length);
      if (!this.input || !this.output || !this.messages.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.speed = Math.max(25, Number(this.dataset.speed) || 65);
      this.hold = Math.max(1000, Number(this.dataset.pause) || 2500);
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.refresh = this.sync.bind(this);
      ['focus', 'blur', 'input', 'change'].forEach(event => this.input.addEventListener(event, this.refresh, options));
      document.addEventListener('visibilitychange', this.refresh, options);
      this.motion.addEventListener('change', this.refresh, options);
      this.observer = new ResizeObserver(() => {
        this.align();
        this.sync();
      });
      this.observer.observe(this.input);
      this.align();
      this.sync();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
      this.input?.classList.remove('smx-search-messages-active');
    }

    available() {
      return !document.hidden && this.input.value.length === 0 && document.activeElement !== this.input && this.input.getClientRects().length > 0;
    }

    align() {
      const input = this.input.getBoundingClientRect();
      const field = this.parentElement.getBoundingClientRect();
      const style = getComputedStyle(this.input);
      this.style.left = Math.max(0, input.left - field.left + parseFloat(style.paddingLeft)) + 'px';
      this.style.right = Math.max(0, field.right - input.right + parseFloat(style.paddingRight)) + 'px';
      this.style.font = style.font;
      this.style.letterSpacing = style.letterSpacing;
      this.style.direction = style.direction;
    }

    sync() {
      clearTimeout(this.timer);
      const active = this.available();
      this.hidden = !active;
      this.input.classList.toggle('smx-search-messages-active', active);
      if (!active) return;
      this.messageIndex = 0;
      this.position = 0;
      this.deleting = false;
      if (this.motion.matches) {
        this.output.textContent = this.messages[0].join('');
        return;
      }
      this.output.textContent = '';
      this.timer = setTimeout(() => this.tick(), 350);
    }

    tick() {
      if (!this.available()) {
        this.sync();
        return;
      }
      const message = this.messages[this.messageIndex];
      this.position += this.deleting ? -1 : 1;
      this.output.textContent = message.slice(0, this.position).join('');
      let delay = this.deleting ? Math.max(20, this.speed * 0.55) : this.speed;
      if (!this.deleting && this.position >= message.length) {
        this.deleting = true;
        delay = this.hold;
      } else if (this.deleting && this.position <= 0) {
        this.deleting = false;
        this.messageIndex = (this.messageIndex + 1) % this.messages.length;
        delay = 350;
      }
      this.timer = setTimeout(() => this.tick(), delay);
    }
  });
}
